import { Box, Chip, Paper, Typography } from '@mui/material'
import { useState } from 'react'

interface RubroDetectado {
  idRubro: number
  nombre: string
  confidence: number
}

interface MarcaDetectada {
  brand: string
  confidence: number
  rank: number
  bbox?: [number, number, number, number]
}

interface Resultado {
  fotoUrl: string
  rubrosDetectados?: RubroDetectado[]
  marcasDetectadas?: MarcaDetectada[]
}

interface Props {
  detalle: string
}

/* eslint-disable react/prop-types */
const FotoCard: React.FC<{ resultado: Resultado }> = ({ resultado }) => {
  const [imgDims, setImgDims] = useState<{ w: number; h: number } | null>(null)

  const hasMarcas = (resultado.marcasDetectadas?.length ?? 0) > 0
  const hasRubros = (resultado.rubrosDetectados?.length ?? 0) > 0

  return (
    <Paper variant="outlined" sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.75, borderRadius: 2 }}>
      <Box sx={{ position: 'relative', width: '100%', lineHeight: 0 }}>
        <img
          src={resultado.fotoUrl}
          alt="Foto del producto"
          style={{ width: '100%', display: 'block', borderRadius: 6 }}
          onLoad={(e) => {
            const img = e.currentTarget
            setImgDims({ w: img.naturalWidth, h: img.naturalHeight })
          }}
        />

        {imgDims &&
          hasMarcas &&
          resultado.marcasDetectadas!.map((m, i) => {
            if (!m.bbox) return null
            const [x1, y1, x2, y2] = m.bbox
            const left = (x1 / imgDims.w) * 100
            const top = (y1 / imgDims.h) * 100
            const width = ((x2 - x1) / imgDims.w) * 100
            const height = ((y2 - y1) / imgDims.h) * 100

            return (
              <Box
                key={i}
                sx={{
                  position: 'absolute',
                  left: `${left}%`,
                  top: `${top}%`,
                  width: `${width}%`,
                  height: `${height}%`
                }}
              >
                {/* rectángulo */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    border: '2px solid #FF6B35',
                    borderRadius: '4px',
                    pointerEvents: 'none'
                  }}
                />
                {/* label */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-22px',
                    left: 0,
                    bgcolor: '#FF6B35',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: 700,
                    px: '6px',
                    py: '2px',
                    borderRadius: '4px',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.4
                  }}
                >
                  {m.brand} · {Math.round(m.confidence * 100)}%
                </Box>
              </Box>
            )
          })}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {hasRubros &&
          [...resultado.rubrosDetectados!]
            .sort((a, b) => b.confidence - a.confidence)
            .map((r, i) => (
              <Chip
                key={`rubro-${r.idRubro}-${i}`}
                label={`${r.nombre} · ${parseFloat((r.confidence * 100).toFixed(3)).toString()}%`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{
                  whiteSpace: 'nowrap',
                  maxWidth: 'none',
                  '& .MuiChip-label': {
                    whiteSpace: 'nowrap',
                    overflow: 'visible',
                    textOverflow: 'unset'
                  }
                }}
              />
            ))}
        {hasMarcas &&
          [...resultado.marcasDetectadas!]
            .sort((a, b) => b.confidence - a.confidence)
            .map((m, i) => (
              <Chip
                key={`marca-${m.brand}-${i}`}
                label={`${m.brand} · ${parseFloat((m.confidence * 100).toFixed(3)).toString()}%`}
                size="small"
                sx={{
                  bgcolor: '#FF6B35',
                  color: 'white',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  maxWidth: 'none',
                  '& .MuiChip-label': {
                    whiteSpace: 'nowrap',
                    overflow: 'visible',
                    textOverflow: 'unset'
                  }
                }}
              />
            ))}
      </Box>
    </Paper>
  )
}

/* eslint-disable react/prop-types */
const ResultadosDeteccionCards: React.FC<Props> = ({ detalle }) => {
  let resultados: Resultado[] = []

  try {
    const parsed = JSON.parse(detalle)
    resultados = Array.isArray(parsed) ? parsed : (parsed.resultados ?? [])
  } catch {
    return (
      <Typography variant="body2" color="error">
        Error al parsear los resultados de detección.
      </Typography>
    )
  }

  if (resultados.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Sin resultados.
      </Typography>
    )
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: `repeat(${Math.min(resultados.length, 5)}, 200px)`
        },
        gap: 1.5,
        overflowX: 'hidden'
      }}
    >
      {resultados.map((r) => (
        <FotoCard key={r.fotoUrl} resultado={r} />
      ))}
    </Box>
  )
}

export default ResultadosDeteccionCards
